/**
 * Single registration entry point for custom Video.js components.
 *
 * This is the ONLY place `videojs.registerComponent` is called in the whole
 * app. The three custom controls are owned by WS-7 and imported here.
 * Registration is idempotent so it survives Vite HMR and React 18 StrictMode
 * double-invoke without throwing "already registered".
 *
 * NOTE: The imports from `./controls/*` are unresolved until WS-7 lands.
 * This is expected per the master build prompt §11.6 (composition roots are
 * placeholder-free from day one and will not compile until dependencies
 * land). Do NOT stub them. WS-7 must export each component as a named
 * `videojs.Component` subclass:
 *   - `controls/SettingsMenuButton.ts`  → `export class SettingsMenuButton`
 *   - `controls/SyncStatusIndicator.ts` → `export class SyncStatusIndicator`
 */
import videojs from 'video.js';
import type Component from 'video.js/dist/types/component';
// WHY: video.js base skin is imported in main.tsx (top-level entry point)
// to ensure Vite bundles it. Importing CSS in a .ts module that's only
// reached via a function call can be tree-shaken by Vite in some configs.

import { SettingsMenuButton } from './controls/SettingsMenuButton';
import { SyncStatusIndicator } from './controls/SyncStatusIndicator';

/** Registered name of the settings gear + panel (matches controlBarConfig). */
export const SETTINGS_MENU_BUTTON_NAME = 'SettingsMenuButton';
/** Registered name of the sync-status indicator (matches controlBarConfig). */
export const SYNC_STATUS_INDICATOR_NAME = 'SyncStatusIndicator';

/**
 * Whether a component is already registered under `name`.
 *
 * WHY: video.js 8's bundled types declare `getComponent()` as returning
 * `typeof Component` (never undefined), but at runtime it returns
 * `undefined` when the name is unknown. We cast to a nullable shape so the
 * existence check is not flagged as an impossible comparison and so HMR
 * re-evaluation (which resets module state) does not throw on re-register.
 */
function isComponentRegistered(name: string): boolean {
  const existing = videojs.getComponent(name) as unknown as object | undefined;
  return existing !== undefined;
}

/**
 * Register all custom Video.js components idempotently. Safe to call
 * repeatedly (HMR, StrictMode, multiple `VideoPlayer` mounts): each name is
 * only passed to `registerComponent` if it is not already present.
 *
 * @see videojs.registerComponent — name must be unique per app instance.
 */
export function registerComponents(): void {
  // WHY: registerComponent expects `typeof Component` (the constructor). Our
  // custom classes extend `videojs.getComponent('Button')`, which is itself
  // narrowed from `typeof Component` via an `unknown` cast in each control
  // module (see SettingsMenuButton.ts etc.) — TS can't statically relate the two
  // resulting constructor types, so bridge through `unknown` here too.
  if (!isComponentRegistered(SETTINGS_MENU_BUTTON_NAME)) {
    videojs.registerComponent(SETTINGS_MENU_BUTTON_NAME, SettingsMenuButton as unknown as typeof Component);
  }
  if (!isComponentRegistered(SYNC_STATUS_INDICATOR_NAME)) {
    videojs.registerComponent(SYNC_STATUS_INDICATOR_NAME, SyncStatusIndicator as unknown as typeof Component);
  }
}
